import * as React from "react";
import { Box, Typography, Stack, IconButton, Chip, CircularProgress, Button, Alert } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import RadarRoundedIcon from "@mui/icons-material/RadarRounded";
import SettingsInputAntennaRoundedIcon from "@mui/icons-material/SettingsInputAntennaRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import {
  fetchLaunchOptionExtras,
  fetchMyGroupTle,
  fetchMyLaunch,
  fetchMyUplinkStatus,
  launchGroupRocket,
  saveGroupTle,
} from "../api/queries";
import PhotoCredit from "./uplink/PhotoCredit";
import { youTubeEmbedUrl, youTubeId } from "./uplink/youtube";
import AscentView from "./uplink/AscentView";
import {
  COAST_S,
  COUNTDOWN_S,
  ORBIT_MISSION_S,
  TRACKING_DELAY_S,
  missionSecondsAt,
  poweredSeconds,
  tleAfterLaunch,
  type LaunchTleDetails,
} from "./uplink/ascent";
import { meanMotionRevPerDay, orbitalPeriodMinutes } from "./uplink/tle";
import type { MyLaunch } from "../types/launch";
import type { UplinkStatus } from "../types/workshop";

const MONO = "'Roboto Mono', 'Courier New', monospace";

const panelSx = {
  borderRadius: 3,
  p: { xs: 2.25, sm: 3 },
  border: "1px solid rgba(168,85,247,0.24)",
  background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
  backdropFilter: "blur(14px)",
};

const primaryButtonSx = {
  bgcolor: "#7C3AED",
  "&:hover": { bgcolor: "#6D28D9" },
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 2,
  px: 3,
  py: 1.2,
  fontSize: 15,
  "&.Mui-disabled": { bgcolor: "rgba(124,58,237,0.25)", color: "rgba(255,255,255,0.4)" },
};

const labelSx = {
  fontFamily: MONO,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "rgba(196,181,253,0.65)",
};

type Phase = "pad" | "countdown" | "flight" | "separated";

// Real seconds after liftoff during which the launch video is still shown.
const MAX_VIDEO_AFTER_LIFTOFF_S = 30 * 60;

function Fact(props: { label: string; value: string }) {
  return (
    <Stack spacing={0.3}>
      <Typography sx={labelSx}>{props.label}</Typography>
      <Typography sx={{ color: "#FFFFFF", fontSize: 13.5, lineHeight: 1.45 }}>{props.value}</Typography>
    </Stack>
  );
}

// The group's launch, after the hardware design: their chosen rocket on the
// pad, a countdown any member can start, then the flight simulation from
// liftoff -- with the rocket's launch video playing alongside until the
// target orbit is reached -- and the separation of the satellite. Then tracking delivers the
// satellite's TLE, and the group moves on to contacting it. Everything runs
// from the moment the countdown started (stored for the group), so all
// members see the same thing at the same time -- also anyone who opens the
// page later.
export default function LaunchPage(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const [uplink, setUplink] = React.useState<UplinkStatus | null>(null);
  const [launch, setLaunch] = React.useState<MyLaunch | null>(null);
  const [tle, setTle] = React.useState<{ line1: string; line2: string } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [imageCredit, setImageCredit] = React.useState("");
  const [tleDetails, setTleDetails] = React.useState<LaunchTleDetails | undefined>(undefined);
  // The launch option's YouTube clip, if it has one (preferred over an
  // uploaded video), and the player address, fixed when the clip starts.
  const [youtube, setYoutube] = React.useState<{ id: string; startS: number; endS: number | null } | null>(null);
  const [youtubeSrc, setYoutubeSrc] = React.useState<string | null>(null);
  // Server time minus this computer's time, so screens with a wrong clock
  // still run in step with the rest of the group.
  const clockOffset = React.useRef(0);
  const [now, setNow] = React.useState(() => Date.now());
  // Length of the launch video once the browser knows it. Powered flight is
  // stretched to it, so footage and simulation reach orbit together.
  const [videoSeconds, setVideoSeconds] = React.useState<number | null>(null);
  const [videoFailed, setVideoFailed] = React.useState(false);
  const [needsSoundClick, setNeedsSoundClick] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const applyLaunch = React.useCallback((data: MyLaunch) => {
    clockOffset.current = new Date(data.server_now).getTime() - Date.now();
    setLaunch(data);
  }, []);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      const { data: status } = await fetchMyUplinkStatus();
      if (!mounted) return;
      if (!status?.uplink_unlocked) {
        handlePageChange(status?.plan_submitted ? "UplinkWaiting" : "MissionView");
        return;
      }
      setUplink(status);

      const [{ data, error: launchError }, { data: saved }] = await Promise.all([fetchMyLaunch(), fetchMyGroupTle()]);
      if (!mounted) return;
      setLoading(false);
      if (launchError || !data) {
        setError(launchError?.message ?? "Your launch couldn't be loaded.");
        return;
      }
      applyLaunch(data);
      if (saved?.line1 && saved.line2) setTle({ line1: saved.line1, line2: saved.line2 });
      if (data.launch_option_id) {
        fetchLaunchOptionExtras(data.launch_option_id).then(({ data: option }) => {
          if (!mounted || !option) return;
          setImageCredit(option.image_credit ?? "");
          const id = youTubeId(option.video_youtube_url ?? "");
          if (id) setYoutube({ id, startS: option.video_start_s ?? 0, endS: option.video_end_s });
          setTleDetails({
            intlDesignator: option.tle_intl_designator ?? "",
            catalogBase: option.tle_catalog_base,
            raanDeg: option.tle_raan_deg,
            argPerigeeDeg: option.tle_arg_perigee_deg,
            meanAnomalyDeg: option.tle_mean_anomaly_deg,
            bstar: option.tle_bstar,
            meanMotionDot: option.tle_mean_motion_dot,
          });
        });
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(timer);
  }, []);

  // Until the launch starts, check now and then whether another member has
  // started the countdown.
  const launchedAt = launch?.launched_at ? new Date(launch.launched_at).getTime() : null;
  React.useEffect(() => {
    if (!launch || launchedAt !== null) return;
    const timer = window.setInterval(async () => {
      const { data } = await fetchMyLaunch();
      if (data?.launched_at) applyLaunch(data);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [launch, launchedAt, applyLaunch]);

  const elapsed = launchedAt === null ? null : (now + clockOffset.current - launchedAt) / 1000;
  const sinceLiftoff = elapsed === null ? 0 : elapsed - COUNTDOWN_S;
  const hasYoutube = youtube !== null;
  const hasVideo = !hasYoutube && Boolean(launch?.video_url) && !videoFailed;
  // With an end set for the clip, powered flight lasts exactly that long, so
  // the simulation reaches orbit when the footage does; the video itself
  // keeps playing.
  const poweredS = poweredSeconds(
    hasYoutube ? (youtube.endS !== null ? youtube.endS - youtube.startS : null) : hasVideo ? videoSeconds : null,
  );
  const separationRealS = poweredS + COAST_S;
  const missionS = missionSecondsAt(sinceLiftoff, poweredS);
  const phase: Phase =
    elapsed === null ? "pad" : elapsed < COUNTDOWN_S ? "countdown" : sinceLiftoff < separationRealS ? "flight" : "separated";
  const sinceSeparation = sinceLiftoff - separationRealS;
  // The launch footage runs beside the simulation from liftoff and stays
  // after orbit is reached, so the group can keep watching while tracking
  // and contacting the satellite below. Someone opening the page much later
  // doesn't get it any more.
  const videoOn = (hasVideo || hasYoutube) && (phase === "flight" || phase === "separated") && sinceLiftoff < MAX_VIDEO_AFTER_LIFTOFF_S;

  // The YouTube player starts at liftoff -- or, for someone joining late, at
  // the matching point of the clip -- and then plays on freely.
  React.useEffect(() => {
    if (!youtube) return;
    if (videoOn && !youtubeSrc) {
      setYoutubeSrc(youTubeEmbedUrl(youtube.id, youtube.startS + Math.max(0, Math.floor(sinceLiftoff)), null));
    } else if (!videoOn && youtubeSrc) {
      setYoutubeSrc(null);
    }
  }, [youtube, videoOn, youtubeSrc, sinceLiftoff]);

  // Start (or catch up with) an uploaded video at T-0 and let it play to its
  // end. Browsers only allow sound after a click on the page, so members who
  // didn't press the button may first get it muted, with a button to turn
  // the sound on.
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!videoOn) {
      if (!video.paused) video.pause();
      return;
    }
    // Finished: leave it on its last frame instead of starting over.
    if (video.ended || (videoSeconds !== null && sinceLiftoff >= videoSeconds)) return;
    if (Math.abs(video.currentTime - sinceLiftoff) > 1.5) video.currentTime = Math.max(0, sinceLiftoff);
    if (video.paused) {
      video.play().catch(() => {
        video.muted = true;
        setNeedsSoundClick(true);
        video.play().catch(() => undefined);
      });
    }
  }, [videoOn, sinceLiftoff, videoSeconds]);

  const startLaunch = async () => {
    setBusy(true);
    setError(null);
    const { data: startedAt, error: launchError } = await launchGroupRocket();
    setBusy(false);
    if (launchError || !startedAt) {
      setError(launchError?.message ?? "The launch couldn't be started. Did your group choose a launch on its mission sheet?");
      return;
    }
    const { data } = await fetchMyLaunch();
    if (data) applyLaunch(data);
  };

  const getTrackingData = async () => {
    if (!launch || launchedAt === null || launch.orbit_altitude_km === null || launch.orbit_inclination_deg === null) return;
    setBusy(true);
    setError(null);
    const lines = tleAfterLaunch({
      groupId: launch.group_id,
      launchedAt: new Date(launchedAt),
      launchSiteLonDeg: launch.launch_site_lon,
      orbitAltitudeKm: launch.orbit_altitude_km,
      orbitInclinationDeg: launch.orbit_inclination_deg,
      orbitEccentricity: launch.orbit_eccentricity ?? 0.001,
      details: tleDetails,
    });
    const { error: saveError } = await saveGroupTle(lines.line1, lines.line2);
    setBusy(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setTle(lines);
  };

  const satelliteName = uplink ? `${uplink.group_name}-SAT` : "Your satellite";
  const countdown = elapsed === null ? COUNTDOWN_S : Math.max(0, Math.ceil(COUNTDOWN_S - elapsed));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "#08050b",
        backgroundImage:
          "linear-gradient(180deg, rgba(9,5,11,0.95) 0%, rgba(9,5,11,0.88) 30%, rgba(46,16,84,0.55) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: { xs: 2.5, sm: 4, md: 6 },
        pt: { xs: 9, sm: 9, md: 7 },
        pb: { xs: 4, sm: 5, md: 7 },
      }}
    >
      <Stack spacing={{ xs: 3, md: 3.5 }} sx={{ width: "100%", maxWidth: 1000, position: "relative", zIndex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={() => handlePageChange("Dashboard")}
            aria-label="Back to dashboard"
            sx={{
              color: "#E9D5FF",
              border: "1px solid rgba(168,85,247,0.35)",
              bgcolor: "rgba(255,255,255,0.04)",
              "&:hover": { bgcolor: "rgba(168,85,247,0.16)" },
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <Stack spacing={0.25}>
            <Typography
              sx={{
                color: "#C4B5FD",
                fontWeight: 800,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontSize: { xs: 10.5, sm: 12 },
              }}
            >
              Launch{uplink ? ` · ${uplink.mission_name}` : ""}
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 30 } }}>
              {launch?.vehicle || launch?.name || "Launch"}
            </Typography>
          </Stack>
          {uplink && (
            <Chip
              icon={<GroupsRoundedIcon sx={{ fontSize: 17, color: "#E9D5FF !important" }} />}
              label={`Group ${uplink.group_name}`}
              sx={{
                ml: "auto !important",
                bgcolor: "rgba(168,85,247,0.18)",
                color: "#E9D5FF",
                border: "1px solid rgba(168,85,247,0.4)",
                fontWeight: 800,
                fontSize: 12.5,
              }}
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress sx={{ color: "#C4B5FD" }} />
          </Stack>
        ) : launch && !launch.launch_option_id ? (
          <Box sx={panelSx}>
            <Typography sx={{ color: "rgba(226,232,240,0.8)", fontSize: 14, lineHeight: 1.6 }}>
              Your group didn't choose a launch on its mission sheet, so there's no rocket to launch. Please ask our team.
            </Typography>
          </Box>
        ) : launch ? (
          <>
            {phase === "pad" && (
              <Box sx={panelSx}>
                <Stack spacing={2.5}>
                  {launch.image_url && (
                    <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
                      <Box
                        component="img"
                        src={launch.image_url}
                        alt={`${launch.vehicle || launch.name} launch`}
                        sx={{ width: "100%", maxHeight: 320, objectFit: "cover", display: "block" }}
                      />
                      <PhotoCredit credit={imageCredit} />
                    </Box>
                  )}
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 18, sm: 20 } }}>
                      {launch.name} is ready on the pad
                    </Typography>
                    <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                      Your satellite is integrated and waiting for liftoff. Anyone in your group can start the countdown; everyone in the
                      group sees the launch at the same time.
                    </Typography>
                  </Stack>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
                    <Fact label="Rocket" value={[launch.vehicle, launch.provider].filter(Boolean).join(" · ")} />
                    <Fact label="Launch site" value={launch.launch_site || "—"} />
                    <Fact
                      label="Target orbit"
                      value={`${Math.round(launch.orbit_altitude_km ?? 0)} km · ${launch.orbit_inclination_deg}°`}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={busy ? undefined : <RocketLaunchRoundedIcon />}
                    onClick={startLaunch}
                    disabled={busy}
                    sx={{ ...primaryButtonSx, alignSelf: { xs: "stretch", sm: "flex-start" } }}
                  >
                    {busy ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Start the countdown"}
                  </Button>
                </Stack>
              </Box>
            )}

            {(phase === "countdown" || phase === "flight" || phase === "separated") && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: videoOn ? "minmax(0, 1.35fr) minmax(0, 1fr)" : "1fr" },
                  gap: { xs: 2, md: 2.5 },
                  alignItems: "start",
                }}
              >
                {phase === "countdown" ? (
                  <Box sx={{ ...panelSx, textAlign: "center", py: { xs: 6, sm: 8 } }}>
                    <Typography sx={{ ...labelSx, fontSize: 13 }}>Countdown</Typography>
                    <Typography
                      aria-live="assertive"
                      sx={{ fontFamily: MONO, fontWeight: 800, color: "#FFFFFF", fontSize: { xs: 72, sm: 120 }, lineHeight: 1.1 }}
                    >
                      T-{countdown}
                    </Typography>
                    <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: 14 }}>
                      {launch.vehicle || launch.name} · {launch.launch_site}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={panelSx}>
                    <Stack spacing={2}>
                      <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 18 } }}>
                        {phase === "separated"
                          ? `${satelliteName} is in orbit!`
                          : missionS < ORBIT_MISSION_S
                            ? "Liftoff! On the way to orbit"
                            : "Target orbit reached, preparing the separation"}
                      </Typography>
                      <AscentView missionS={missionS} orbitAltitudeKm={launch.orbit_altitude_km ?? 500} satelliteName={satelliteName} />
                    </Stack>
                  </Box>
                )}

                {/* Mounted from the countdown on, so the video has loaded by T-0; shown
                    beside the simulation (above it on phones) during powered flight. */}
                {(hasVideo || hasYoutube) && (phase === "countdown" || videoOn) && (
                  <Box
                    key="launch-video"
                    sx={{
                      ...panelSx,
                      p: 0,
                      overflow: "hidden",
                      position: { xs: "relative", md: "sticky" },
                      top: { md: 16 },
                      order: { xs: -1, md: 0 },
                      display: videoOn ? "block" : "none",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ px: 1.75, py: 1, borderBottom: "1px solid rgba(168,85,247,0.2)" }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#F87171", boxShadow: "0 0 8px #F87171" }} />
                      <Typography sx={{ ...labelSx, color: "#FCA5A5" }}>Launch footage</Typography>
                      <Typography noWrap sx={{ color: "rgba(226,232,240,0.7)", fontSize: 12.5 }}>
                        {[launch.vehicle, launch.provider].filter(Boolean).join(" · ")}
                      </Typography>
                    </Stack>
                    {hasYoutube ? (
                      youtubeSrc && (
                        <Box sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9", bgcolor: "#000" }}>
                          <Box
                            component="iframe"
                            src={youtubeSrc}
                            title={`Launch video: ${[launch.vehicle, launch.provider].filter(Boolean).join(" · ")}`}
                            allow="autoplay; encrypted-media; picture-in-picture"
                            referrerPolicy="strict-origin-when-cross-origin"
                            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                          />
                        </Box>
                      )
                    ) : (
                      <Box sx={{ position: "relative" }}>
                        <Box
                          component="video"
                          ref={videoRef}
                          src={launch.video_url ?? undefined}
                          preload="auto"
                          playsInline
                          onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) =>
                            setVideoSeconds(
                              Number.isFinite(e.currentTarget.duration) && e.currentTarget.duration > 0 ? e.currentTarget.duration : null,
                            )
                          }
                          onError={() => setVideoFailed(true)}
                          sx={{ display: "block", width: "100%", maxHeight: "60vh", bgcolor: "#000" }}
                        />
                        {needsSoundClick && (
                          <Button
                            startIcon={<VolumeUpRoundedIcon />}
                            onClick={() => {
                              if (videoRef.current) videoRef.current.muted = false;
                              setNeedsSoundClick(false);
                            }}
                            sx={{
                              position: "absolute",
                              right: 12,
                              bottom: 12,
                              textTransform: "none",
                              fontWeight: 800,
                              color: "#FFFFFF",
                              bgcolor: "rgba(9,5,11,0.75)",
                              "&:hover": { bgcolor: "rgba(9,5,11,0.9)" },
                            }}
                          >
                            Turn sound on
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {phase === "separated" && !tle && (
              <Box sx={panelSx}>
                <Stack spacing={2}>
                  <Stack spacing={0.75}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 18 } }}>Where exactly is it?</Typography>
                    <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                      Your satellite was released into roughly the planned orbit, but to talk to it, mission control needs to know precisely
                      where it is. Radar tracking measures the new objects in orbit and publishes their orbits as TLEs (two-line element
                      sets).
                    </Typography>
                  </Stack>
                  {sinceSeparation < TRACKING_DELAY_S ? (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CircularProgress size={18} sx={{ color: "#C4B5FD" }} />
                      <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: 13.5 }}>
                        Tracking stations are measuring the new objects…
                      </Typography>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={busy ? undefined : <RadarRoundedIcon />}
                      onClick={getTrackingData}
                      disabled={busy}
                      sx={{ ...primaryButtonSx, alignSelf: { xs: "stretch", sm: "flex-start" } }}
                    >
                      {busy ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Get tracking data"}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {phase === "separated" && tle && (
              <Box sx={{ ...panelSx, border: "1px solid rgba(74,222,128,0.35)" }}>
                <Stack spacing={2}>
                  <Stack spacing={0.75}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 18 } }}>
                      Tracking data for {satelliteName}
                    </Typography>
                    <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                      This is your satellite's TLE. Mission control uses it to predict when your satellite passes over the ground station;
                      only then can you contact it and send your command.
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "rgba(9,5,11,0.65)",
                      border: "1px solid rgba(168,85,247,0.3)",
                      overflowX: "auto",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{ m: 0, fontFamily: MONO, fontSize: { xs: 11.5, sm: 13 }, color: "#E9D5FF", lineHeight: 1.7 }}
                    >
                      {tle.line1}
                      {"\n"}
                      {tle.line2}
                    </Typography>
                  </Box>
                  {launch.orbit_altitude_km !== null && (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
                      <Fact label="Altitude" value={`about ${Math.round(launch.orbit_altitude_km)} km`} />
                      <Fact label="Inclination" value={`${tle.line2.slice(8, 16).trim()}°`} />
                      <Fact label="One orbit takes" value={`${orbitalPeriodMinutes(launch.orbit_altitude_km).toFixed(1)} min`} />
                      <Fact label="Orbits per day" value={meanMotionRevPerDay(launch.orbit_altitude_km).toFixed(2)} />
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SettingsInputAntennaRoundedIcon />}
                    onClick={() => handlePageChange("CommandExecutor")}
                    sx={{ ...primaryButtonSx, alignSelf: { xs: "stretch", sm: "flex-start" } }}
                  >
                    Contact the satellite
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
